import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Plus, Trash2, Save, Send, ChevronDown, Users, Truck,
  CheckCircle, XCircle, ArrowLeft, Calendar, ClipboardList
} from "lucide-react";

const SHIFTS = ["MAÑANA", "TARDE", "NOCHE"];
const CATEGORIES = ["PEÓN", "CONDUCTOR", "P. CONDUCTOR", "INSPECTOR", "OFICIAL", "ENCARGADO", "JEFE DE EQUIPO"];
const SERVICE_TYPES = [
  "BARRIDO MANUAL", "BARRIDO MANUAL MOTORIZADO", "BARRIDO MECÁNICO",
  "BALDEO MANUAL", "BALDEO MIXTO", "BALDEO MECÁNICO DE CALZADAS",
  "RECOGIDA Y TRANSPORTE FRACCIÓN RESTO CARGA LATERAL",
  "RECOGIDA Y TRANSPORTE DE BASURA DISPERSA",
  "RECOGIDA FRACCIÓN ORGÁNICA", "RECOGIDA SELECTIVA",
  "RECOGIDA Y LAVADO CONTENEDORES",
  "INSPECCIÓN", "INTERVENCIÓN INMEDIATA", "LAVADERO", "LIMPIEZA PLAYAS"
];
const DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

const newEntry = () => ({
  shift: "MAÑANA",
  service_type: "BARRIDO MANUAL",
  vehicle_plate: "",
  dedication_pct: 100,
  personnel_name: "",
  category: "PEÓN",
  substitute: "",
  sector_route: "",
  completed: true,
  observations: ""
});

export default function WorkReportEntry() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { authAxios } = useAuth();

  const [contracts, setContracts] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [catalogServices, setCatalogServices] = useState([]);
  const [catalogCategories, setCatalogCategories] = useState([]);
  const [contractId, setContractId] = useState(location.state?.contractId || "");
  const [reportDate, setReportDate] = useState(location.state?.date || new Date().toISOString().split("T")[0]);
  const [dayOfWeek, setDayOfWeek] = useState(location.state?.dayOfWeek || "");

  // Pre-fill entries from planning if provided
  const planEntries = location.state?.planEntries;
  const [entries, setEntries] = useState(() => {
    if (planEntries && planEntries.length > 0) {
      return planEntries.map(e => ({
        shift: e.shift || "MAÑANA",
        service_type: e.service_type || "BARRIDO MANUAL",
        vehicle_plate: e.vehicle_plate || "",
        dedication_pct: 100,
        personnel_name: e.personnel_name || "",
        category: e.category || "PEÓN",
        substitute: "",
        sector_route: e.sector_route || "",
        completed: true,
        observations: ""
      }));
    }
    return [newEntry()];
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingId, setExistingId] = useState(null);

  useEffect(() => {
    authAxios.get("/contracts").then(r => {
      setContracts(r.data);
      if (r.data.length > 0 && !contractId) setContractId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (id && id !== "new") {
      authAxios.get(`/work-reports/${id}`).then(r => {
        const report = r.data;
        setContractId(report.contract_id);
        setReportDate(report.date);
        setDayOfWeek(report.day_of_week);
        setEntries(report.entries?.length > 0 ? report.entries : [newEntry()]);
        setExistingId(id);
      });
    }
  }, [id]);

  useEffect(() => {
    if (contractId) {
      authAxios.get(`/personnel?contract_id=${contractId}&active=true`).then(r => setPersonnel(r.data));
      authAxios.get(`/vehicles?contract_id=${contractId}&status=active`).then(r => setVehicles(r.data));
      authAxios.get(`/contracts/${contractId}/catalog`).then(r => {
        setCatalogServices(r.data.services || []);
        setCatalogCategories(r.data.categories || []);
      }).catch(() => {});
    }
  }, [contractId]);

  // helpers for catalog-aware UI
  const allServices = Array.from(new Set([...(catalogServices || []), ...SERVICE_TYPES])).sort();
  const allCategories = Array.from(new Set([...(catalogCategories || []), ...CATEGORIES])).sort();
  const isOutOfCatalog = (val, list) => val && !list.includes(val);

  // ─── Confidence indicator ──────────────────────────────────────────────────
  // Normaliza igual que el backend (NFD + upper + colapsa espacios).
  const norm = (s) => {
    if (!s) return "";
    return s.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .trim().toUpperCase().replace(/\s+/g, " ");
  };
  const normPlate = (s) => (s || "").toString().toUpperCase().replace(/[^A-Z0-9]/g, "");

  // Fuzzy: usa coincidencia de tokens (todos los tokens del valor deben aparecer
  // en algún ítem del catálogo). Suficiente para detectar "GARCIA, JOSE" vs
  // "GARCIA FERNANDEZ, JOSE" sin traer toda la librería difflib al cliente.
  const fuzzyMatch = (raw, options) => {
    if (!raw || !options || options.length === 0) return null;
    const tokens = norm(raw).split(/\s+|,/).filter(t => t.length >= 3);
    if (tokens.length === 0) return null;
    for (const opt of options) {
      const optN = norm(opt);
      if (tokens.every(t => optN.includes(t))) return opt;
    }
    return null;
  };

  const personnelNames = personnel.map(p => p.full_name || `${p.surname || ""}, ${p.name || ""}`).filter(Boolean);
  const vehiclePlates = vehicles.map(v => v.plate).filter(Boolean);

  // confidence(entry) → { service, category, personnel, plate } ∈ {exact|fuzzy|none|empty}
  const rowConfidence = (entry) => {
    const r = {};
    // service
    if (!entry.service_type) r.service = "empty";
    else if (allServices.includes(entry.service_type)) r.service = "exact";
    else if (fuzzyMatch(entry.service_type, allServices)) r.service = "fuzzy";
    else r.service = "none";
    // category
    if (!entry.category) r.category = "empty";
    else if (allCategories.includes(entry.category)) r.category = "exact";
    else if (fuzzyMatch(entry.category, allCategories)) r.category = "fuzzy";
    else r.category = "none";
    // personnel
    if (!entry.personnel_name) r.personnel = "empty";
    else if (personnelNames.some(n => norm(n) === norm(entry.personnel_name))) r.personnel = "exact";
    else if (fuzzyMatch(entry.personnel_name, personnelNames)) r.personnel = "fuzzy";
    else r.personnel = "none";
    // plate
    if (!entry.vehicle_plate) r.plate = "empty";
    else if (vehiclePlates.some(p => normPlate(p) === normPlate(entry.vehicle_plate))) r.plate = "exact";
    else r.plate = "none";
    return r;
  };

  // Overall row badge: red if any "none", yellow if any "fuzzy", green if all exact/empty
  const rowOverall = (conf) => {
    const vals = Object.values(conf);
    if (vals.includes("none")) return "red";
    if (vals.includes("fuzzy")) return "yellow";
    return "green";
  };

  const fieldHighlight = (state) => {
    // returns extra CSS classes for the input wrapper based on confidence
    if (state === "exact" || state === "empty") return "";
    if (state === "fuzzy") return "ring-2 ring-amber-300 ring-offset-1 rounded-lg";
    if (state === "none") return "ring-2 ring-rose-300 ring-offset-1 rounded-lg";
    return "";
  };

  const addToCatalog = async (entity, value) => {
    if (!value || !contractId) return;
    try {
      const r = await authAxios.post(`/contracts/${contractId}/catalog/${entity}`, { value });
      if (entity === "services") setCatalogServices(r.data.services || []);
      if (entity === "categories") setCatalogCategories(r.data.categories || []);
    } catch (e) {
      console.error("addToCatalog failed", e);
    }
  };

  useEffect(() => {
    if (reportDate) {
      const d = new Date(reportDate + "T12:00:00");
      setDayOfWeek(DAYS[d.getDay() === 0 ? 6 : d.getDay() - 1]);
    }
  }, [reportDate]);

  const addEntry = () => setEntries(prev => [...prev, newEntry()]);
  const removeEntry = (i) => setEntries(prev => prev.filter((_, idx) => idx !== i));
  const updateEntry = (i, field, value) => {
    setEntries(prev => prev.map((e, idx) => idx === i ? { ...e, [field]: value } : e));
  };

  const stats = {
    staff: new Set(entries.filter(e => e.personnel_name).map(e => e.personnel_name)).size,
    vehicles: new Set(entries.filter(e => e.vehicle_plate).map(e => e.vehicle_plate)).size,
    completed: entries.filter(e => e.completed).length,
    total: entries.length,
    needs_review: entries.filter(e => rowOverall(rowConfidence(e)) !== "green").length,
  };

  const handleSave = async (status = "draft") => {
    if (!contractId || !reportDate) return;
    setSaving(true);
    const payload = {
      contract_id: contractId,
      date: reportDate,
      day_of_week: dayOfWeek,
      entries: entries.map(e => ({ ...e, dedication_pct: parseInt(e.dedication_pct) || 100, vehicle_plate: e.vehicle_plate || null, substitute: e.substitute || null, observations: e.observations || null })),
      status,
      uploaded_from: "manual"
    };
    try {
      if (existingId) {
        await authAxios.put(`/work-reports/${existingId}`, payload);
      } else {
        const r = await authAxios.post("/work-reports", payload);
        setExistingId(r.data.id);
      }
      if (status === "submitted") navigate("/");
    } finally {
      setSaving(false);
    }
  };

  const selectedContract = contracts.find(c => c.id === contractId);
  const completion = stats.total > 0 ? Math.round(stats.completed / stats.total * 100) : 0;

  return (
    <div className="flex flex-col h-full">
      {/* Sticky header */}
      <div className="glass-header px-6 py-4 border-b border-slate-200 flex-shrink-0 z-20 sticky top-0">
        <div className="flex items-center gap-4 flex-wrap">
          <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-800 transition-colors">
            <ArrowLeft size={18} />
          </button>
          <ClipboardList size={20} className="text-[#007934]" />
          <h1 className="text-lg font-bold text-slate-900">
            {existingId ? "Editar Parte de Trabajo" : "Nuevo Parte de Trabajo"}
          </h1>
          <div className="flex items-center gap-3 ml-auto flex-wrap">
            {/* Contract */}
            <select
              data-testid="contract-select"
              value={contractId}
              onChange={e => setContractId(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20 focus:border-[#007934]"
            >
              {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {/* Date */}
            <input
              data-testid="date-input"
              type="date"
              value={reportDate}
              onChange={e => setReportDate(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20 focus:border-[#007934]"
            />
            {dayOfWeek && <span className="text-sm font-semibold text-[#007934] bg-[#E6F4EA] px-3 py-1.5 rounded-lg">{dayOfWeek}</span>}
            {/* Actions */}
            <button
              data-testid="save-draft-btn"
              onClick={() => handleSave("draft")}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-all"
            >
              <Save size={15} /> Guardar
            </button>
            <button
              data-testid="submit-report-btn"
              onClick={() => handleSave("submitted")}
              disabled={saving || !contractId}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#007934] hover:bg-[#005c27] text-white text-sm font-semibold shadow-md transition-all active:scale-95 disabled:opacity-50"
            >
              <Send size={15} /> Enviar Parte
            </button>
          </div>
        </div>

        {/* Live stats */}
        <div className="flex items-center gap-6 mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <Users size={15} className="text-[#007934]" />
            <span className="text-sm font-semibold text-slate-700">{stats.staff}</span>
            <span className="text-xs text-slate-400">operarios</span>
          </div>
          <div className="flex items-center gap-2">
            <Truck size={15} className="text-[#007934]" />
            <span className="text-sm font-semibold text-slate-700">{stats.vehicles}</span>
            <span className="text-xs text-slate-400">vehículos</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle size={15} className="text-emerald-600" />
            <span className="text-sm font-semibold text-slate-700">{stats.completed}/{stats.total}</span>
            <span className="text-xs text-slate-400">completados</span>
          </div>
          <div className="h-2 flex-1 max-w-32 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${completion}%`, backgroundColor: completion >= 95 ? "#3BAD2D" : completion >= 80 ? "#F59E0B" : "#EF4444" }}
            />
          </div>
          <span className={`text-sm font-bold ${completion >= 95 ? "text-emerald-600" : completion >= 80 ? "text-amber-500" : "text-red-500"}`}>
            {completion}%
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        {/* Confidence legend / review banner */}
        {entries.length > 0 && (
          <div
            className={`mb-3 flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border ${
              stats.needs_review === 0
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-amber-50 border-amber-200 text-amber-900"
            }`}
            data-testid="confidence-banner"
          >
            <div className="flex items-center gap-2 text-xs font-semibold">
              {stats.needs_review === 0 ? (
                <span>✓ Todas las {stats.total} filas coinciden con el catálogo del contrato.</span>
              ) : (
                <span>
                  ⚠ {stats.needs_review} de {stats.total} filas necesitan revisión antes de exportar.
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-[11px] text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Exacto</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Parcial</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" /> No canónico</span>
            </div>
          </div>
        )}

        <div className="card-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0B2818] text-white text-xs uppercase tracking-wider">
                  <th className="px-2 py-3 w-8 text-center font-semibold" title="Confianza de validación">
                    <span className="sr-only">Confianza</span>
                    <span aria-hidden="true">●</span>
                  </th>
                  <th className="px-3 py-3 text-left font-semibold">Turno</th>
                  <th className="px-3 py-3 text-left font-semibold">Servicio</th>
                  <th className="px-3 py-3 text-left font-semibold">Matrícula</th>
                  <th className="px-3 py-3 text-left font-semibold w-16">%</th>
                  <th className="px-3 py-3 text-left font-semibold">Nombre</th>
                  <th className="px-3 py-3 text-left font-semibold">Categoría</th>
                  <th className="px-3 py-3 text-left font-semibold">Sustituto</th>
                  <th className="px-3 py-3 text-left font-semibold">Sector / Ruta</th>
                  <th className="px-3 py-3 text-center font-semibold">OK</th>
                  <th className="px-3 py-3 text-left font-semibold">Observaciones</th>
                  <th className="px-3 py-3 w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.map((entry, i) => {
                  const conf = rowConfidence(entry);
                  const overall = rowOverall(conf);
                  const dotColor = { green: "bg-emerald-500", yellow: "bg-amber-400", red: "bg-rose-500" }[overall];
                  const dotTitle = { green: "Todo coincide con el catálogo",
                                     yellow: "Coincidencia parcial — revisa los campos resaltados",
                                     red: "Hay valores no canónicos — revisa antes de exportar" }[overall];
                  return (
                  <tr
                    key={i}
                    data-testid={`entry-row-${i}`}
                    data-confidence={overall}
                    className={`transition-colors ${entry.completed ? "hover:bg-slate-50" : "bg-red-50/40 hover:bg-red-50"}`}
                  >
                    {/* Confidence dot */}
                    <td className="px-2 py-2 text-center align-middle">
                      <span
                        className={`inline-block w-2.5 h-2.5 rounded-full ${dotColor} shadow-sm`}
                        title={dotTitle}
                        data-testid={`entry-conf-${i}-${overall}`}
                        aria-label={dotTitle}
                      />
                    </td>
                    {/* Turno */}
                    <td className="px-3 py-2">
                      <select
                        value={entry.shift}
                        onChange={e => updateEntry(i, "shift", e.target.value)}
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white w-24 focus:outline-none focus:ring-1 focus:ring-[#007934]"
                      >
                        {SHIFTS.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                    {/* Servicio */}
                    <td className="px-3 py-2">
                      <div className="relative">
                        <select
                          value={entry.service_type}
                          onChange={e => updateEntry(i, "service_type", e.target.value)}
                          className={`text-xs border rounded-lg px-2 py-1.5 bg-white w-52 focus:outline-none focus:ring-1 focus:ring-[#007934] ${isOutOfCatalog(entry.service_type, allServices) ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}
                        >
                          {entry.service_type && !allServices.includes(entry.service_type) && (
                            <option value={entry.service_type}>⚠ {entry.service_type} (no en catálogo)</option>
                          )}
                          {allServices.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        {isOutOfCatalog(entry.service_type, allServices) && (
                          <button
                            type="button"
                            title={`Añadir "${entry.service_type}" al catálogo del contrato`}
                            onClick={() => addToCatalog("services", entry.service_type)}
                            className="absolute -right-1 -top-1 w-5 h-5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-bold shadow"
                          >+</button>
                        )}
                      </div>
                    </td>
                    {/* Matrícula */}
                    <td className="px-3 py-2">
                      <div className={`relative ${fieldHighlight(conf.plate)}`}>
                        <input
                          list={`vehicles-${i}`}
                          value={entry.vehicle_plate}
                          onChange={e => updateEntry(i, "vehicle_plate", e.target.value)}
                          placeholder="Matrícula"
                          className={`text-xs border rounded-lg px-2 py-1.5 w-28 font-mono focus:outline-none focus:ring-1 focus:ring-[#007934] ${conf.plate === "none" ? "border-rose-400 bg-rose-50" : conf.plate === "fuzzy" ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}
                          title={conf.plate === "none" ? "Matrícula no canónica — no figura en la flota del contrato" : ""}
                        />
                        <datalist id={`vehicles-${i}`}>
                          {vehicles.map(v => <option key={v.id} value={v.plate}>{v.plate} – {v.brand || ""} {v.model_name || v.type || ""}</option>)}
                        </datalist>
                      </div>
                    </td>
                    {/* % */}
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min={1}
                        max={100}
                        value={entry.dedication_pct}
                        onChange={e => updateEntry(i, "dedication_pct", e.target.value)}
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 w-14 text-center focus:outline-none focus:ring-1 focus:ring-[#007934]"
                      />
                    </td>
                    {/* Nombre */}
                    <td className="px-3 py-2">
                      <input
                        list={`personnel-${i}`}
                        value={entry.personnel_name}
                        onChange={e => updateEntry(i, "personnel_name", e.target.value)}
                        placeholder="APELLIDO, Nombre"
                        className={`text-xs border rounded-lg px-2 py-1.5 w-44 focus:outline-none focus:ring-1 focus:ring-[#007934] ${entry.personnel_name && !personnel.some(p => p.full_name === entry.personnel_name) ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}
                        title={entry.personnel_name && !personnel.some(p => p.full_name === entry.personnel_name) ? "Persona no encontrada en el contrato" : ""}
                      />
                      <datalist id={`personnel-${i}`}>
                        {personnel.map(p => <option key={p.id} value={p.full_name}>{p.full_name} – {p.category}</option>)}
                      </datalist>
                    </td>
                    {/* Categoría */}
                    <td className="px-3 py-2">
                      <div className="relative">
                        <select
                          value={entry.category}
                          onChange={e => updateEntry(i, "category", e.target.value)}
                          className={`text-xs border rounded-lg px-2 py-1.5 bg-white w-32 focus:outline-none focus:ring-1 focus:ring-[#007934] ${isOutOfCatalog(entry.category, allCategories) ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}
                        >
                          {entry.category && !allCategories.includes(entry.category) && (
                            <option value={entry.category}>⚠ {entry.category}</option>
                          )}
                          {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {isOutOfCatalog(entry.category, allCategories) && (
                          <button
                            type="button"
                            title={`Añadir "${entry.category}" al catálogo`}
                            onClick={() => addToCatalog("categories", entry.category)}
                            className="absolute -right-1 -top-1 w-5 h-5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-bold shadow"
                          >+</button>
                        )}
                      </div>
                    </td>
                    {/* Sustituto */}
                    <td className="px-3 py-2">
                      <input
                        list={`personnel-sub-${i}`}
                        value={entry.substitute}
                        onChange={e => updateEntry(i, "substitute", e.target.value)}
                        placeholder="Sustituto de..."
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 w-36 focus:outline-none focus:ring-1 focus:ring-[#007934]"
                      />
                      <datalist id={`personnel-sub-${i}`}>
                        {personnel.map(p => <option key={p.id} value={p.full_name} />)}
                      </datalist>
                    </td>
                    {/* Sector/Ruta */}
                    <td className="px-3 py-2">
                      <input
                        value={entry.sector_route}
                        onChange={e => updateEntry(i, "sector_route", e.target.value)}
                        placeholder="SECTOR 1 / RUTA A"
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 w-36 focus:outline-none focus:ring-1 focus:ring-[#007934]"
                      />
                    </td>
                    {/* OK/KO */}
                    <td className="px-3 py-2 text-center">
                      <button
                        data-testid={`toggle-completed-${i}`}
                        onClick={() => updateEntry(i, "completed", !entry.completed)}
                        className="transition-all"
                      >
                        {entry.completed
                          ? <CheckCircle size={20} className="text-emerald-500 hover:text-emerald-600" />
                          : <XCircle size={20} className="text-red-400 hover:text-red-500" />
                        }
                      </button>
                    </td>
                    {/* Observaciones */}
                    <td className="px-3 py-2">
                      <input
                        value={entry.observations || ""}
                        onChange={e => updateEntry(i, "observations", e.target.value)}
                        placeholder="Notas..."
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 w-36 focus:outline-none focus:ring-1 focus:ring-[#007934]"
                      />
                    </td>
                    {/* Delete */}
                    <td className="px-2 py-2">
                      <button
                        data-testid={`remove-entry-${i}`}
                        onClick={() => removeEntry(i)}
                        disabled={entries.length === 1}
                        className="text-slate-300 hover:text-red-400 transition-colors disabled:opacity-30"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>

          {/* Add row */}
          <div className="p-3 border-t border-slate-100">
            <button
              data-testid="add-entry-btn"
              onClick={addEntry}
              className="flex items-center gap-2 text-sm text-[#007934] hover:text-[#005c27] font-medium transition-colors px-3 py-2 rounded-lg hover:bg-[#E6F4EA]"
            >
              <Plus size={16} /> Añadir fila
            </button>
          </div>
        </div>

        {/* Summary by shift */}
        <div className="mt-4 grid grid-cols-3 gap-4">
          {SHIFTS.map(shift => {
            const shiftEntries = entries.filter(e => e.shift === shift);
            if (shiftEntries.length === 0) return null;
            const done = shiftEntries.filter(e => e.completed).length;
            return (
              <div key={shift} className="card-white p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${shift === "MAÑANA" ? "bg-amber-50 text-amber-700" : shift === "TARDE" ? "bg-orange-50 text-orange-700" : "bg-slate-100 text-slate-600"}`}>
                    {shift}
                  </span>
                  <span className="text-xs text-slate-400">{done}/{shiftEntries.length}</span>
                </div>
                <p className="text-xl font-black text-slate-800">{shiftEntries.length}</p>
                <p className="text-xs text-slate-400">entradas · {new Set(shiftEntries.filter(e => e.personnel_name).map(e => e.personnel_name)).size} personas</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
